/** What the Users screen says about an account, decided once.
 *
 * An account's state is two columns — `disabled_at`, set by an admin, and
 * `deleted_at`, set by the account itself — and the screen shows one word.
 * Deleted wins: a deleted account cannot upload whatever `disabled_at` says,
 * and the row only exists so its source ids can never be re-claimed, so
 * offering to enable it would be offering nothing.
 */

import type { TagTone } from '@/components/ui';
import type { AccountPaceFilter, AccountState } from '@/lib/types';

/** In the order the filter shows them. */
export const ACCOUNT_STATES: readonly AccountState[] = ['active', 'disabled', 'deleted'];

export function isAccountState(value: string): value is AccountState {
  return (ACCOUNT_STATES as readonly string[]).includes(value);
}

export function accountState(account: { disabled: boolean; deleted_at: string | null }): AccountState {
  if (account.deleted_at) return 'deleted';
  return account.disabled ? 'disabled' : 'active';
}

/** Active wears nothing — it is the ordinary case — and disabled wears the
 *  error bed, because it is an admin's decision standing against an account. */
export function accountStateTone(state: AccountState): TagTone {
  switch (state) {
    case 'active':
      return 'quiet';
    case 'disabled':
      return 'danger';
    case 'deleted':
      return 'neutral';
  }
}

/** `github:octocat`, or the provider alone once the login has been
 *  anonymised by a deletion. */
export function identityLabel(identity: { provider: string; provider_login: string }): string {
  return identity.provider_login
    ? `${identity.provider}:${identity.provider_login}`
    : `${identity.provider} · anonymised`;
}

// ── upload pace ─────────────────────────────────────────────────────────────

/** In the order the filter shows them. */
export const PACE_FILTERS: readonly AccountPaceFilter[] = ['limited', 'own'];

export function isPaceFilter(value: string): value is AccountPaceFilter {
  return (PACE_FILTERS as readonly string[]).includes(value);
}

/** `60 s`, or the words for the one value that is not a wait. */
export function intervalLabel(seconds: number): string {
  return seconds <= 0 ? 'no wait' : `${seconds} s`;
}

/**
 * What the interval field holds, read as an interval: whole seconds from 0 to
 * `max`, or `null` for anything else. An empty field is not zero — no wait at
 * all is something an admin types, not something they get by clearing a box.
 */
export function parseInterval(text: string, max: number): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value <= max ? value : null;
}
